import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, IconButton, Button, Chip, Divider } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
    ArrowBackOutlined, NorthEastOutlined, SouthWestOutlined,
    SearchOutlined, FilterListOutlined, LockOutlined,
    PhoneIphoneOutlined, WifiOutlined, FlashOnOutlined,
    AccountBalanceOutlined, SwapHorizOutlined, ShoppingBagOutlined,
    ReceiptOutlined,
} from '@mui/icons-material';

const B = {
    deep:    '#0D0820',
    dark:    '#1A0F3D',
    mid:     '#2D1B69',
    purple:  '#7B5EA7',
    light:   '#9B7EC8',
    lavender:'#F7F4FC',
    ink:     '#0D0A1A',
    green:   '#00C896',
    greenDim:'#00C89618',
    red:     '#FF4D6A',
    redDim:  '#FF4D6A18',
    orange:  '#FF9500',
    blue:    '#007AFF',
};

type TxType = 'all' | 'credit' | 'debit';
type TxCategory = 'all' | 'airtime' | 'data' | 'bills' | 'transfer' | 'funding' | 'shopping';

interface Transaction {
    id: number;
    type: 'credit' | 'debit';
    category: TxCategory;
    title: string;
    sub: string;
    amount: number;
    date: string;
    dateGroup: string;
    status: 'success' | 'pending' | 'failed';
}

const ALL_TXS: Transaction[] = [
    { id: 1,  type: 'credit', category: 'funding',  title: 'Salary Credit',       sub: 'Access Bank',         amount: 80000, date: 'Today · 9:14 AM',      dateGroup: 'Today',      status: 'success' },
    { id: 2,  type: 'debit',  category: 'airtime',  title: 'MTN Airtime',          sub: '080 *** 4521',        amount: 1000,  date: 'Today · 8:02 AM',      dateGroup: 'Today',      status: 'success' },
    { id: 3,  type: 'debit',  category: 'data',     title: 'Airtel Data Bundle',   sub: '081 *** 3310 · 1.5GB',amount: 1000,  date: 'Today · 7:30 AM',      dateGroup: 'Today',      status: 'success' },
    { id: 4,  type: 'debit',  category: 'bills',    title: 'Electricity Bill',     sub: 'EKEDC · 01234567890', amount: 15000, date: 'Yesterday · 6:45 PM',  dateGroup: 'Yesterday',  status: 'success' },
    { id: 5,  type: 'credit', category: 'funding',  title: 'Wallet Funding',       sub: 'GTBank',              amount: 20000, date: 'Yesterday · 2:30 PM',  dateGroup: 'Yesterday',  status: 'success' },
    { id: 6,  type: 'debit',  category: 'transfer', title: 'Transfer · Emeka O.',  sub: 'Zenith Bank',         amount: 5000,  date: 'Yesterday · 11:00 AM', dateGroup: 'Yesterday',  status: 'success' },
    { id: 7,  type: 'debit',  category: 'shopping', title: 'Shoprite Purchase',    sub: 'Card Payment',        amount: 8500,  date: 'Mon · 3:22 PM',        dateGroup: 'This Week',  status: 'pending' },
    { id: 8,  type: 'debit',  category: 'airtime',  title: 'Glo Airtime',          sub: '081 *** 7722',        amount: 200,   date: 'Mon · 10:45 AM',       dateGroup: 'This Week',  status: 'success' },
    { id: 9,  type: 'debit',  category: 'bills',    title: 'DSTV Subscription',    sub: 'Smart Card · 789 ***',amount: 8000,  date: 'Sun · 2:10 PM',        dateGroup: 'This Week',  status: 'success' },
    { id: 10, type: 'credit', category: 'funding',  title: 'Wallet Funding',       sub: 'First Bank',          amount: 50000, date: 'Sun · 9:00 AM',        dateGroup: 'This Week',  status: 'success' },
    { id: 11, type: 'debit',  category: 'data',     title: 'MTN Data Bundle',      sub: '080 *** 4521 · 5GB',  amount: 1500,  date: '14 Mar · 8:15 AM',     dateGroup: 'Earlier',    status: 'success' },
    { id: 12, type: 'debit',  category: 'transfer', title: 'Transfer · Adaeze N.', sub: 'UBA',                 amount: 10000, date: '12 Mar · 4:00 PM',     dateGroup: 'Earlier',    status: 'success' },
    { id: 13, type: 'debit',  category: 'bills',    title: 'Internet Bill',        sub: 'Spectranet · Renewal',amount: 12000, date: '10 Mar · 9:00 AM',     dateGroup: 'Earlier',    status: 'success' },
    { id: 14, type: 'debit',  category: 'shopping', title: 'Amazon Purchase',      sub: 'Card Payment',        amount: 22000, date: '8 Mar · 1:35 PM',      dateGroup: 'Earlier',    status: 'failed'  },
    { id: 15, type: 'credit', category: 'funding',  title: 'Refund · Amazon',      sub: 'Card Reversal',       amount: 22000, date: '9 Mar · 11:20 AM',     dateGroup: 'Earlier',    status: 'success' },
];

const FILTERS: { label: string; value: TxType }[] = [
    { label: 'All',     value: 'all'    },
    { label: 'Credits', value: 'credit' },
    { label: 'Debits',  value: 'debit'  },
];

const CAT_FILTERS: { label: string; value: TxCategory; emoji: string }[] = [
    { label: 'All',      value: 'all',      emoji: '📋' },
    { label: 'Airtime',  value: 'airtime',  emoji: '📱' },
    { label: 'Data',     value: 'data',     emoji: '📶' },
    { label: 'Bills',    value: 'bills',    emoji: '⚡' },
    { label: 'Transfer', value: 'transfer', emoji: '💸' },
    { label: 'Funding',  value: 'funding',  emoji: '💰' },
    { label: 'Shopping', value: 'shopping', emoji: '🛍️' },
];

const getCatIcon = (cat: TxCategory, color: string) => {
    const props = { sx: { color, fontSize: 16 } };
    switch (cat) {
        case 'airtime':  return <PhoneIphoneOutlined {...props} />;
        case 'data':     return <WifiOutlined {...props} />;
        case 'bills':    return <FlashOnOutlined {...props} />;
        case 'transfer': return <SwapHorizOutlined {...props} />;
        case 'funding':  return <AccountBalanceOutlined {...props} />;
        case 'shopping': return <ShoppingBagOutlined {...props} />;
        default:         return <ReceiptOutlined {...props} />;
    }
};

const fmt = (n: number) =>
    new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(n);

const TransactionHistoryPage: React.FC = () => {
    const navigate = useNavigate();
    const theme    = useTheme();
    const isDark   = theme.palette.mode === 'dark';

    const [typeFilter, setTypeFilter] = useState<TxType>('all');
    const [catFilter,  setCatFilter]  = useState<TxCategory>('all');
    const [search,     setSearch]     = useState('');
    const [showSearch, setShowSearch] = useState(false);

    const pageBg     = isDark ? B.deep : B.lavender;
    const surfaceBg  = isDark ? alpha(B.mid, .55) : '#fff';
    const surfaceBdr = `1px solid ${alpha(isDark ? '#fff' : B.dark, .07)}`;
    const surfaceShd = `0 4px 28px ${alpha(B.dark, isDark ? .25 : .06)}`;
    const textPri    = isDark ? '#fff' : B.ink;
    const textMid    = alpha(isDark ? '#fff' : B.ink, .45);
    const textFaint  = alpha(isDark ? '#fff' : B.ink, .28);
    const dividerC   = alpha(isDark ? '#fff' : B.dark, .08);

    const glassCard = {
        background: surfaceBg, border: surfaceBdr, boxShadow: surfaceShd,
        borderRadius: '20px', backdropFilter: isDark ? 'blur(20px)' : 'none',
    };

    const filtered = useMemo(() => {
        return ALL_TXS.filter(tx => {
            if (typeFilter !== 'all' && tx.type !== typeFilter) return false;
            if (catFilter  !== 'all' && tx.category !== catFilter) return false;
            if (search && !tx.title.toLowerCase().includes(search.toLowerCase()) && !tx.sub.toLowerCase().includes(search.toLowerCase())) return false;
            return true;
        });
    }, [typeFilter, catFilter, search]);

    const totalIn  = filtered.filter(t => t.type === 'credit').reduce((s, t) => s + t.amount, 0);
    const totalOut = filtered.filter(t => t.type === 'debit').reduce((s, t) => s + t.amount, 0);

    // Group by dateGroup preserving order
    const groups = useMemo(() => {
        const order: string[] = [];
        const map: Record<string, Transaction[]> = {};
        filtered.forEach(tx => {
            if (!map[tx.dateGroup]) { map[tx.dateGroup] = []; order.push(tx.dateGroup); }
            map[tx.dateGroup].push(tx);
        });
        return order.map(g => ({ label: g, txs: map[g] }));
    }, [filtered]);

    const statusColor = (s: string) =>
        s === 'success' ? B.green : s === 'pending' ? B.orange : B.red;

    return (
        <Box sx={{ minHeight: '100vh', background: pageBg, pb: 10, fontFamily: "'DM Sans', system-ui, sans-serif" }}>

            {/* ══ HERO ══ */}
            <Box sx={{
                background: `linear-gradient(160deg, ${B.deep} 0%, ${B.mid} 55%, ${B.purple} 100%)`,
                mt: { xs: '-76px', sm: 0 },
                pt: { xs: '80px', sm: '28px' },
                pb: { xs: 4, sm: 14 }, px: 3, position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -70, right: -50, width: 240, height: 240, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.light, .18)} 0%, transparent 70%)`, pointerEvents: 'none' }} />
                <Box sx={{ position: 'absolute', bottom: -10, left: -40, width: 200, height: 200, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.purple, .12)} 0%, transparent 70%)`, pointerEvents: 'none' }} />

                <Box display="flex" alignItems="center" gap={1.5}>
                    <IconButton onClick={() => navigate(-1)}
                                sx={{ color: alpha('#fff', .7), background: alpha('#fff', .1), border: `1px solid ${alpha('#fff', .15)}`, width: 36, height: 36, '&:hover': { background: alpha('#fff', .2) } }}>
                        <ArrowBackOutlined sx={{ fontSize: 17 }} />
                    </IconButton>
                    <Box flex={1}>
                        <Typography sx={{ color: alpha('#fff', .35), fontSize: '.62rem', letterSpacing: '.18em', textTransform: 'uppercase' }}>
                            NG Credit
                        </Typography>
                        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem', letterSpacing: '-.025em', fontFamily: "'Instrument Serif', serif" }}>
                            Transaction History
                        </Typography>
                    </Box>
                    <IconButton onClick={() => setShowSearch(s => !s)}
                                sx={{ color: alpha('#fff', .7), background: alpha('#fff', .1), border: `1px solid ${alpha('#fff', .15)}`, width: 36, height: 36, '&:hover': { background: alpha('#fff', .2) } }}>
                        <SearchOutlined sx={{ fontSize: 17 }} />
                    </IconButton>
                </Box>
            </Box>

            <Container maxWidth="sm" sx={{ mt: { xs: 2, sm: -8 }, position: 'relative', zIndex: 1, px: { xs: 2, sm: 3 } }}>

                {/* ── Search ── */}
                {showSearch && (
                    <Box sx={{ ...glassCard, p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <SearchOutlined sx={{ color: textFaint, fontSize: 18 }} />
                        <input
                            autoFocus
                            placeholder="Search transactions…"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            style={{
                                flex: 1, background: 'transparent', border: 'none', outline: 'none',
                                color: textPri, fontSize: '.9rem', fontFamily: "'DM Sans', system-ui, sans-serif",
                            }}
                        />
                        {search && (
                            <IconButton size="small" onClick={() => setSearch('')} sx={{ color: textFaint, p: .5 }}>
                                ×
                            </IconButton>
                        )}
                    </Box>
                )}

                {/* ── Summary strip ── */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
                    <Box sx={{ ...glassCard, p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: .5 }}>
                            <Box sx={{ width: 28, height: 28, borderRadius: '8px', background: B.greenDim, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <SouthWestOutlined sx={{ color: B.green, fontSize: 14 }} />
                            </Box>
                            <Typography sx={{ color: textFaint, fontSize: '.65rem', fontWeight: 600, letterSpacing: '.05em' }}>MONEY IN</Typography>
                        </Box>
                        <Typography sx={{ color: B.green, fontWeight: 700, fontSize: '1rem', fontFamily: "'Instrument Serif', serif", fontVariantNumeric: 'tabular-nums' }}>
                            +{fmt(totalIn)}
                        </Typography>
                    </Box>
                    <Box sx={{ ...glassCard, p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: .5 }}>
                            <Box sx={{ width: 28, height: 28, borderRadius: '8px', background: B.redDim, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <NorthEastOutlined sx={{ color: B.red, fontSize: 14 }} />
                            </Box>
                            <Typography sx={{ color: textFaint, fontSize: '.65rem', fontWeight: 600, letterSpacing: '.05em' }}>MONEY OUT</Typography>
                        </Box>
                        <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '1rem', fontFamily: "'Instrument Serif', serif", fontVariantNumeric: 'tabular-nums' }}>
                            −{fmt(totalOut)}
                        </Typography>
                    </Box>
                </Box>

                {/* ── Type filter ── */}
                <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                    {FILTERS.map(f => (
                        <Box key={f.value} onClick={() => setTypeFilter(f.value)} sx={{
                            px: 2, py: .75, borderRadius: '20px', cursor: 'pointer', fontSize: '.75rem', fontWeight: 600,
                            background: typeFilter === f.value ? `linear-gradient(135deg, ${B.purple}, ${B.light})` : alpha(isDark ? '#fff' : B.dark, .05),
                            color: typeFilter === f.value ? '#fff' : textMid,
                            border: `1px solid ${typeFilter === f.value ? 'transparent' : dividerC}`,
                            boxShadow: typeFilter === f.value ? `0 4px 14px ${alpha(B.purple, .35)}` : 'none',
                            transition: 'all .18s',
                        }}>
                            {f.label}
                        </Box>
                    ))}
                </Box>

                {/* ── Category filter scroll ── */}
                <Box sx={{ display: 'flex', gap: 1, mb: 2.5, overflowX: 'auto', pb: .5, '&::-webkit-scrollbar': { display: 'none' } }}>
                    {CAT_FILTERS.map(f => (
                        <Box key={f.value} onClick={() => setCatFilter(f.value)} sx={{
                            flexShrink: 0, px: 1.5, py: .65, borderRadius: '20px', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: .6,
                            background: catFilter === f.value ? alpha(B.purple, .12) : alpha(isDark ? '#fff' : B.dark, .04),
                            color: catFilter === f.value ? B.light : textMid,
                            border: `1px solid ${catFilter === f.value ? alpha(B.purple, .35) : dividerC}`,
                            fontSize: '.72rem', fontWeight: 600, transition: 'all .18s',
                        }}>
                            <span style={{ fontSize: '.9rem' }}>{f.emoji}</span> {f.label}
                        </Box>
                    ))}
                </Box>

                {/* ── Transaction groups ── */}
                {groups.length === 0 ? (
                    <Box sx={{ ...glassCard, p: 5, textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '2rem', mb: 1 }}>🔍</Typography>
                        <Typography sx={{ color: textMid, fontWeight: 600, fontSize: '.9rem' }}>No transactions found</Typography>
                        <Typography sx={{ color: textFaint, fontSize: '.78rem', mt: .5 }}>Try adjusting your filters</Typography>
                    </Box>
                ) : (
                    groups.map(group => (
                        <Box key={group.label} sx={{ mb: 2.5 }}>
                            <Typography sx={{ color: textFaint, fontSize: '.6rem', letterSpacing: '.15em', textTransform: 'uppercase', fontWeight: 700, mb: 1.25, px: .5 }}>
                                {group.label}
                            </Typography>
                            <Box sx={{ ...glassCard, overflow: 'hidden' }}>
                                {group.txs.map((tx, i) => {
                                    const isCredit = tx.type === 'credit';
                                    const iconBg   = isCredit ? B.greenDim : B.redDim;
                                    const iconColor = isCredit ? B.green : B.red;
                                    return (
                                        <React.Fragment key={tx.id}>
                                            <Box sx={{
                                                px: 2.5, py: 1.85, display: 'flex', alignItems: 'center', gap: 2,
                                                cursor: 'pointer', transition: 'background .15s',
                                                '&:hover': { background: alpha(isDark ? '#fff' : B.dark, .025) },
                                            }}>
                                                <Box sx={{ width: 42, height: 42, borderRadius: 2.5, flexShrink: 0, background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                                                    {getCatIcon(tx.category, iconColor)}
                                                </Box>
                                                <Box flex={1} minWidth={0}>
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <Typography sx={{ fontWeight: 600, fontSize: '.84rem', color: textPri, letterSpacing: '-.015em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                            {tx.title}
                                                        </Typography>
                                                        {tx.status !== 'success' && (
                                                            <Box sx={{ flexShrink: 0, px: .8, py: .2, borderRadius: '6px', background: alpha(statusColor(tx.status), .12), border: `1px solid ${alpha(statusColor(tx.status), .25)}` }}>
                                                                <Typography sx={{ color: statusColor(tx.status), fontSize: '.55rem', fontWeight: 700, letterSpacing: '.04em' }}>
                                                                    {tx.status.toUpperCase()}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                    <Typography sx={{ fontSize: '.68rem', color: textFaint, mt: .15 }}>
                                                        {tx.sub} · {tx.date}
                                                    </Typography>
                                                </Box>
                                                <Typography sx={{
                                                    fontWeight: 700, fontSize: '.86rem', flexShrink: 0,
                                                    fontVariantNumeric: 'tabular-nums',
                                                    color: isCredit ? B.green : textPri,
                                                }}>
                                                    {isCredit ? '+' : '−'}₦{tx.amount.toLocaleString()}
                                                </Typography>
                                            </Box>
                                            {i < group.txs.length - 1 && <Divider sx={{ mx: 2.5, borderColor: alpha(isDark ? '#fff' : B.dark, .055) }} />}
                                        </React.Fragment>
                                    );
                                })}
                            </Box>
                        </Box>
                    ))
                )}

                {/* ── Load more ── */}
                {groups.length > 0 && (
                    <Button fullWidth
                            sx={{ py: 1.4, borderRadius: '14px', textTransform: 'none', fontWeight: 600, fontSize: '.85rem', color: textMid, border: surfaceBdr, background: surfaceBg, backdropFilter: 'blur(20px)', mt: .5, '&:hover': { background: alpha(B.purple, .07), color: textPri } }}>
                        Load more transactions
                    </Button>
                )}

                {/* footer */}
                <Box sx={{ mt: 3.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: .75 }}>
                    <LockOutlined sx={{ fontSize: 11, color: alpha(isDark ? '#fff' : B.ink, .18) }} />
                    <Typography sx={{ fontSize: '.62rem', color: alpha(isDark ? '#fff' : B.ink, .18), letterSpacing: '.05em' }}>
                        256-BIT ENCRYPTION · NG CREDIT FINANCIAL SERVICES
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default TransactionHistoryPage;